import React, { useState } from 'react'
import { useTheme } from '@/hooks/useTheme'
import { usePagosAdmin, Pago } from '@/hooks/usePagosAdmin'
import { useReservationsAdmin } from '@/hooks/useReservationsAdmin'
import { formatDateDisplay } from '@/utils/dateHelpers'
import { FiltrosReporte } from '@/services/reportService'

interface PaymentsReportTableProps {
  filtros: FiltrosReporte
}

const PaymentsReportTable: React.FC<PaymentsReportTableProps> = ({ filtros }) => {
  const { colors } = useTheme()
  const [page, setPage] = useState(0)
  const pageSize = 20

  // Obtener pagos con filtros de fecha
  const { data: pagoResponse, isLoading: loadingPagos, error: errorPagos } = usePagosAdmin({
    fecha_desde: filtros.fecha_inicio,
    fecha_hasta: filtros.fecha_fin,
    limit: pageSize,
    offset: page * pageSize
  })

  // Obtener reservas para mapear información
  const { data: reservasResponse } = useReservationsAdmin({
    fecha_desde: filtros.fecha_inicio,
    fecha_hasta: filtros.fecha_fin,
    limit: 1000 // Obtener todas las reservas del período
  })

  const pagos = pagoResponse?.pagos || []
  const total = pagoResponse?.total || 0
  const totalPages = Math.ceil(total / pageSize)

  // Mapeo de reservas por ID
  const reservasMap = new Map(
    (reservasResponse?.reservas || []).map((r) => [r.id, r])
  )

  const getEstadoColor = (estado: string) => {
    if (estado === 'aprobado' || estado === 'pagado') {
      return { bg: `${colors.primary}15`, text: colors.primary }
    } else if (estado === 'pendiente') {
      return { bg: `${colors.primaryLight}15`, text: colors.primaryLight }
    } else {
      return { bg: '#fee2e2', text: '#dc2626' }
    }
  }

  const formatearEstado = (estado: string) => {
    return estado.charAt(0).toUpperCase() + estado.slice(1)
  }

  const formatDateShort = (dateStr: string) => {
    if (!dateStr) return '-'
    // Si viene en formato ISO (con T), extraer la parte de fecha
    const datePart = dateStr.split('T')[0]
    const [year, month, day] = datePart.split('-')
    return `${day}/${month}/${year}`
  }

  const [grouped, setGrouped] = React.useState(false)

  const handleExport = () => {
    if (pagos.length === 0) return

    const csv = [
      ['ID Reserva', 'Cliente', 'Método', 'Monto', 'Moneda', 'Fecha', 'Estado'].join(','),
      ...pagos.map((p: Pago) => {
        const reserva = reservasMap.get(p.reserva_id)
        const clienteNombre = reserva?.cliente_nombre || reserva?.huesped_nombre || '-'
        return [
          p.reserva_id.slice(0, 8),
          clienteNombre,
          p.proveedor_pago || 'Manual',
          p.monto,
          p.moneda,
          formatDateShort(p.fecha_creacion),
          p.estado
        ].join(',')
      })
    ].join('\n')

    const blob = new Blob([csv], { type: 'text/csv' })
    const url = window.URL.createObjectURL(blob)
    const a = document.createElement('a')
    a.href = url
    a.download = `pagos-${new Date().toISOString().split('T')[0]}.csv`
    document.body.appendChild(a)
    a.click()
    window.URL.revokeObjectURL(url)
    document.body.removeChild(a)
  }

  if (errorPagos) {
    return (
      <div className="bg-white rounded-lg shadow-md p-6">
        <p className="text-red-600">Error al cargar los pagos</p>
      </div>
    )
  }

  return (
    <div className="bg-white rounded-lg shadow-md p-6">
      <div className="mb-4 flex items-center justify-between">
        <h3 className="text-lg font-bold text-gray-900">
          Pagos ({total} total)
        </h3>
        <div className="flex items-center gap-3">
          {loadingPagos && <span className="text-sm text-gray-500">Cargando...</span>}
          <button
            onClick={() => setGrouped(!grouped)}
            className="px-2 py-1 text-2xl hover:bg-gray-100 transition-all rounded"
            title={grouped ? 'Expandir' : 'Contraer'}
          >
            {grouped ? '+' : '−'}
          </button>
          <button
            onClick={handleExport}
            disabled={pagos.length === 0}
            className="px-3 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium hover:bg-gray-50 transition-all disabled:opacity-50 disabled:cursor-not-allowed text-sm"
            title="Exportar a CSV"
          >
            📥 CSV
          </button>
        </div>
      </div>

      {/* Tabla */}
      {!grouped && (
      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="border-b border-gray-200">
              <th className="text-left py-3 px-4 font-semibold text-gray-900">ID Reserva</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Cliente</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Método</th>
              <th className="text-right py-3 px-4 font-semibold text-gray-900">Monto</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Moneda</th>
              <th className="text-left py-3 px-4 font-semibold text-gray-900">Fecha</th>
              <th className="text-center py-3 px-4 font-semibold text-gray-900">Estado</th>
            </tr>
          </thead>
          <tbody>
            {pagos.length === 0 ? (
              <tr>
                <td colSpan={7} className="py-8 text-center text-gray-500">
                  No hay pagos en este período
                </td>
              </tr>
            ) : (
              pagos.map((pago: Pago) => {
                const reserva = reservasMap.get(pago.reserva_id)
                const estadoColor = getEstadoColor(pago.estado)
                const clienteNombre = reserva?.cliente_nombre || reserva?.huesped_nombre || '-'

                return (
                  <tr key={pago.id} className="border-b border-gray-100 hover:bg-gray-50">
                    <td className="py-3 px-4 text-gray-900 font-medium font-mono text-xs cursor-help" title={pago.reserva_id}>
                      {pago.reserva_id.slice(0, 8)}
                    </td>
                    <td className="py-3 px-4 text-gray-700">{clienteNombre}</td>
                    <td className="py-3 px-4 text-gray-700">
                      {pago.proveedor_pago || 'Manual'}
                    </td>
                    <td className="py-3 px-4 text-right text-gray-900 font-medium">
                      ${pago.monto.toLocaleString('es-CL')}
                    </td>
                    <td className="py-3 px-4 text-gray-700">{pago.moneda}</td>
                    <td className="py-3 px-4 text-gray-700">
                      {formatDateShort(pago.fecha_creacion)}
                    </td>
                    <td className="py-3 px-4 text-center">
                      <span
                        className="px-2 py-1 rounded text-xs font-semibold"
                        style={{
                          backgroundColor: estadoColor.bg,
                          color: estadoColor.text
                        }}
                      >
                        {formatearEstado(pago.estado)}
                      </span>
                    </td>
                  </tr>
                )
              })
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Paginación */}
      {!grouped && totalPages > 1 && (
        <div className="mt-6 flex items-center justify-between">
          <div className="text-sm text-gray-500">
            Página {page + 1} de {totalPages} ({total} registros)
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(Math.max(0, page - 1))}
              disabled={page === 0}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              ← Anterior
            </button>
            <button
              onClick={() => setPage(Math.min(totalPages - 1, page + 1))}
              disabled={page === totalPages - 1}
              className="px-4 py-2 rounded-lg border border-gray-300 text-gray-700 font-medium disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-50"
            >
              Siguiente →
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

export default PaymentsReportTable
